import { normalizeKeyStatus } from "../constants/app";
import type { AccountManager, BusinessManager } from "../constants/org";
import { monthName } from "./getMonth";

/**
 * Response helpers for solutions-dashboard-service (Go) API data.
 *
 * Components call `callApi` directly. These utilities do not perform HTTP.
 *
 * | Resource      | Use API fields as-is? | Notes |
 * |---------------|----------------------|-------|
 * | Organisation  | Yes                  | `organisation_id`, `org_name`, `onboarding_status`, `created_at` |
 * | App           | Yes                  | `app_id`, `app_name`, `test_app`, `products_access`, `created_at` |
 * | App key       | Yes                  | `key_id`, `key_name`, `api_key`, `status`, `created_at` |
 * | ZPIN          | Yes                  | `zpin_id`, `user_id`, `created_at` |
 * | List payloads | `unwrapListData`     | Go returns `{ apps: [...] }`, `{ keys: [...] }`, `{ zpins: [...] }` inside `data` |
 */

type TimestampValue = number | string | undefined;

/**
 * Go `created_at` / `updated_at` are Unix seconds.
 * UI formatters convert to local display strings at render time.
 */
function apiTimestampToMillis(
  value: TimestampValue
): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    if (numeric <= 0) return undefined;
    const millis = numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
    if (millis < Date.UTC(2000, 0, 1)) return undefined;
    return millis;
  }
  const parsed = Date.parse(String(value));
  if (!Number.isFinite(parsed) || parsed < Date.UTC(2000, 0, 1)) return undefined;
  return parsed;
}

/** Formats a Go API timestamp for display (e.g. "June 18, 2026"). */
export function formatApiDate(value: TimestampValue): string {
  const millis = apiTimestampToMillis(value);
  if (!millis) return "";
  const date = new Date(millis);
  if (date.getFullYear() < 2000) return "";
  return `${monthName(date.getMonth())} ${date.getDate()}, ${date.getFullYear()}`;
}

function readNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

/** Returns the canonical product PID from a catalog/org-product/subscribe record. */
export function productPID(record: Record<string, unknown> = {}): string {
  const nested = record.product as Record<string, unknown> | undefined;
  return readNonEmptyString(
    record.product_pid,
    nested?.product_pid,
    record.products_pid,
    record.product_id,
    nested?.product_id
  );
}

/** Reads organisation_id from browser session storage. */
export function getStoredOrganisationId(): string {
  return readNonEmptyString(localStorage.getItem("organisation_id"));
}

/** Builds the SDS subscribe/unsubscribe request body ({ product_id }). */
export function buildProductSubscribePayload(productId: string): { product_id: string } {
  const id = readNonEmptyString(productId);
  if (!id) {
    throw new Error("Product ID is missing for this catalogue item.");
  }
  return { product_id: id };
}

/** Returns the canonical product ARN from a catalog/org-product record. */
export function productARN(record: Record<string, unknown> = {}): string {
  return (record.product_arn as string) || "";
}

/** Returns the canonical product display name from a catalog/org-product record. */
export function productName(record: Record<string, unknown> = {}): string {
  const nested = record.product as Record<string, unknown> | undefined;
  return readNonEmptyString(
    record.product_name,
    nested?.product_name,
    record.name,
    nested?.name
  );
}

/** Admin-stored docs URL. Empty and "NA" mean unset. */
export function productDocumentLink(record: Record<string, unknown> = {}): string {
  const nested = record.product as Record<string, unknown> | undefined;
  const raw = readNonEmptyString(record.document_link, nested?.document_link);
  if (!raw || raw.toUpperCase() === "NA") return "";
  return raw;
}

/** In-app OpenAPI docs path used when document_link is empty. */
export function inAppDocsHref(arn: string, name: string): string {
  const tag = String(arn || "").split("_")[2]?.toUpperCase();
  if (!tag || !name) return "";
  const operation = String(name).trim().toLowerCase().replaceAll(" ", "_");
  if (!operation) return "";
  return `/doc#tag/${encodeURIComponent(tag)}/operation/${encodeURIComponent(operation)}`;
}

/** True for this app's /doc Redoc URLs (relative or any dashboard origin). */
export function isInAppDocsUrl(raw: string): boolean {
  const value = String(raw || "").trim();
  if (!value) return false;
  if (value.startsWith("/doc#") || value.startsWith("/doc?") || value === "/doc") {
    return true;
  }
  try {
    const parsed = new URL(value);
    return parsed.pathname === "/doc" || parsed.pathname.startsWith("/doc/");
  } catch {
    return false;
  }
}

/**
 * External admin document_link (Apidog, Confluence) wins.
 * Stored /doc URLs are ignored so staging/prod both use a relative hash
 * (`/doc#tag/...`) and Redoc can open the operation.
 */
export function productDocsHref(record: Record<string, unknown> = {}): string {
  const stored = productDocumentLink(record);
  if (stored && !isInAppDocsUrl(stored)) return stored;
  return inAppDocsHref(productARN(record), productName(record));
}

/** True when the API error is a ZPIN/OTP mismatch (show it on the auth modal). */
export function isZpinOrOtpError(message: string): boolean {
  const text = (message || "").toLowerCase();
  return (
    text.includes("invalid zpin") ||
    text.includes("zpin is invalid") ||
    text.includes("invalid otp") ||
    text.includes("otp is invalid")
  );
}

/** Replaces product IDs in an API error with catalog names when the UI has them. */
export function replaceProductIdsWithNames(
  message: string,
  products: unknown[] = []
): string {
  if (!message) return message;
  let next = message;
  products.forEach((product) => {
    const record = (product || {}) as Record<string, unknown>;
    const id = productPID(record);
    const name = productName(record);
    if (!id || !name || name === id) return;
    const prefixed = `product ${id}`;
    if (next.includes(prefixed)) {
      next = next.split(prefixed).join(name);
    }
    if (next.includes(id)) {
      next = next.split(id).join(name);
    }
  });
  return next;
}

/** True when the product does not require admin approval before subscription. */
export function isAutoApprovedProduct(record: Record<string, unknown> = {}): boolean {
  return record.is_approval_required === false;
}

/**
 * Builds a product `id -> name` lookup from one or more catalog/org-products
 * responses so `products_access` IDs can be resolved to human-readable names.
 * Later lists win on duplicate IDs; pass subscribed, org, then catalog sources.
 */
export function buildProductNameMap(
  ...productLists: unknown[]
): Record<string, string> {
  const map: Record<string, string> = {};
  productLists.forEach((products) => {
    if (!Array.isArray(products)) return;
    products.forEach((product) => {
      const record = product as Record<string, unknown>;
      const id = productPID(record);
      const name = productName(record);
      if (id && name) map[id] = name;
    });
  });
  return map;
}

/**
 * List/create/update `products_access` is `string[]` (product IDs).
 * GET `/apps/:id` returns catalog objects with `product_pid` / `product_name`.
 * Manage-apps UI expects `{ product_pid, name }[]`.
 */
export function normalizeProductAccess(
  products: unknown,
  nameById: Record<string, string> = {}
) {
  if (!Array.isArray(products)) return [];

  return products
    .map((product) => {
      if (typeof product === "string") {
        return { product_pid: product, name: nameById[product] || "" };
      }

      const record = product as Record<string, unknown>;
      const id = productPID(record);
      const name =
        nameById[id] ||
        productName(record) ||
        (record.name as string) ||
        "";

      return { ...record, product_pid: id, name };
    })
    .filter((product) => {
      const id = String(product.product_pid || "").trim();
      const name = String(product.name || "").trim();
      return Boolean(id) && Boolean(name) && name !== id;
    });
}

/** Default page size for paginated apps list (infinite scroll on manage apps). */
export const APPS_PAGE_LIMIT = 50;

/**
 * Counts app keys per `app_id` from a `GET /keys` list payload so the apps
 * grid can show a keys total when keys are not embedded on the app.
 */
export function countKeysByApp(
  keys: Record<string, unknown>[] = []
): Record<string, number> {
  return keys.reduce<Record<string, number>>((acc, key) => {
    const id = key.app_id as string;
    if (id) acc[id] = (acc[id] ?? 0) + 1;
    return acc;
  }, {});
}

/** Sets `keys_count` from embedded `keys` on each app when present. */
export function mapAppsWithKeyCounts<T extends { app_id?: string; keys?: unknown[]; keys_count?: number }>(
  apps: T[]
): T[] {
  return apps.map((app) => ({
    ...app,
    keys_count:
      typeof app.keys_count === "number"
        ? app.keys_count
        : Array.isArray(app.keys)
          ? app.keys.length
          : 0,
  }));
}

export type ListMetadata = {
  total?: number;
  offset?: number;
  limit?: number;
};

/** Reads list pagination metadata from a Go list response (`data.metadata`). */
export function unwrapListMetadata(data: unknown): ListMetadata | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  const metadata = record.metadata;
  if (metadata && typeof metadata === "object") {
    return metadata as ListMetadata;
  }
  if (record.data && typeof record.data === "object") {
    const nested = (record.data as Record<string, unknown>).metadata;
    if (nested && typeof nested === "object") {
      return nested as ListMetadata;
    }
  }
  return undefined;
}

/**
 * Merges `GET /apps/:id` with keys from `GET /keys?app_ids=…` when keys are not embedded.
 */
export function mergeAppWithKeys(
  app: Record<string, unknown> = {},
  keys: Record<string, unknown>[] = []
) {
  return {
    ...app,
    keys: keys.map((key) => ({
      ...key,
      status: normalizeKeyStatus(key.status as string | undefined),
    })) as Record<string, unknown>[],
    ip_whitelisted: Array.isArray(app.ip_whitelisted)
      ? (app.ip_whitelisted as string[])
      : [],
    products_access: Array.isArray(app.products_access)
      ? app.products_access
      : [],
  };
}

/**
 * Unwraps list payloads from `callApi` / `parseApiResponse`.
 * Handles a bare array or `{ apps | keys | zpins: [...] }` inside `data`.
 */
export function unwrapListData<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    const nested = record[key];
    if (Array.isArray(nested)) return nested as T[];
    if (record.data && typeof record.data === "object") {
      const inner = (record.data as Record<string, unknown>)[key];
      if (Array.isArray(inner)) return inner as T[];
    }
  }
  return [];
}

export type OrgSummaryWallet = {
  wallets_id?: string;
  wallets_balance?: number | string;
  wallets_balance_with_gst?: number | string;
  alert_balance?: number | string;
  hold_amount?: number | string;
  environment?: string;
  wallets_type?: string;
  is_active?: boolean;
};

export type OrgSummary = {
  wallets: OrgSummaryWallet[];
  subscribed_products_count: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function walletFieldEquals(
  wallet: OrgSummaryWallet,
  field: "wallets_type" | "environment",
  expected: string
): boolean {
  return String(wallet[field] ?? "").trim().toLowerCase() === expected;
}

/** Normalizes GET /organisations/:id/summary from a raw envelope or unwrapped `data`. */
export function unwrapOrgSummary(payload: unknown): OrgSummary {
  const root = asRecord(payload);
  const nested = asRecord(root?.data);
  const source = nested && Array.isArray(nested.wallets) ? nested : root;
  const wallets = unwrapListData<OrgSummaryWallet>(source ?? payload, "wallets");
  const count = Number(
    source?.subscribed_products_count ??
      nested?.subscribed_products_count ??
      root?.subscribed_products_count ??
      0
  );
  return {
    wallets,
    subscribed_products_count: Number.isFinite(count) ? count : 0,
  };
}

export function findOrgWallet(
  wallets: OrgSummaryWallet[] | undefined,
  walletsType: string,
  environment: string
): OrgSummaryWallet | undefined {
  const type = walletsType.trim().toLowerCase();
  const env = environment.trim().toLowerCase();
  return (wallets ?? []).find(
    (wallet) =>
      walletFieldEquals(wallet, "wallets_type", type) &&
      walletFieldEquals(wallet, "environment", env)
  );
}

export function walletBalanceWithGst(
  wallet: OrgSummaryWallet | undefined
): number | undefined {
  if (wallet == null) return undefined;
  const raw = wallet.wallets_balance_with_gst ?? wallet.wallets_balance;
  if (raw == null || raw === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/** Reads plan_type from GET /organisation or GET plan-info payloads. */
export function readOrgPlanType(payload: unknown): string {
  const root = asRecord(payload);
  const nested = asRecord(root?.data) ?? root;
  if (!nested) return "";
  const activePlan = asRecord(nested.activePlan) ?? asRecord(nested.active_plan);
  const current = asRecord(nested.current);
  const currentPlan = asRecord(current?.plan);
  return readNonEmptyString(
    nested.plan_type,
    activePlan?.plan_type,
    currentPlan?.plan_type
  );
}

/** SDS list max page size (`constants.Pagination.MaxLimit`). */
export const APPS_LIST_MAX_LIMIT = 200;

/** True when an app payload is the onboarding test/trial app. */
export function isTestApp(app: { test_app?: unknown } | null | undefined): boolean {
  if (!app) return false;
  return app.test_app === true || app.test_app === 1 || app.test_app === "true";
}

/** Returns the test app's `app_id` from a list response, if present. */
export function findTestAppId(apps: Record<string, unknown>[]): string {
  const testApp = apps.find((app) => isTestApp(app));
  return (testApp?.app_id as string) ?? "";
}

/**
 * Coerces API payloads to an array.
 * Legacy billing routes and 404 fallthrough objects from `callApi` are not arrays.
 */
export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export type UsageReportsParseResult = {
  rows: Record<string, unknown>[];
  /** Async email job notice from SDS `data.message`. */
  infoMessage?: string;
};

/**
 * Normalizes GET usage-reports payloads from `callApi` (envelope `data` only).
 * Sync id-only: `{ data: [...] }` or `{ data: "Done" }`. Filtered async: `{ message: "..." }`.
 */
export function parseUsageReportsPayload(payload: unknown): UsageReportsParseResult {
  if (Array.isArray(payload)) {
    return { rows: payload as Record<string, unknown>[] };
  }

  const record = asRecord(payload);
  if (!record) {
    return { rows: [] };
  }

  const infoMessage = readNonEmptyString(record.message);
  const nested = record.data;
  if (Array.isArray(nested)) {
    return { rows: nested as Record<string, unknown>[], infoMessage: infoMessage || undefined };
  }

  return { rows: [], infoMessage: infoMessage || undefined };
}

/** True when `callApi` returned a 404 object instead of throwing. */
export function isApiNotFoundResponse(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { statusCode?: number }).statusCode === 404
  );
}

/** Parses JSON metadata fields that may arrive as objects or JSON strings. */
export function parseApiJsonField<T>(value: unknown, fallback: T): T {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

type OrganisationManagerFields = {
  kam?: AccountManager;
  business_manager?: BusinessManager;
  bm?: BusinessManager;
  org_kam?: AccountManager | string;
  org_business_manager?: BusinessManager | string;
  org_bm?: BusinessManager | string;
};

function trimString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function managerRefObject<T extends Record<string, unknown>>(
  ref: unknown,
  idKey: keyof T
): T | undefined {
  if (ref === undefined || ref === null) return undefined;

  if (typeof ref === "object") {
    return ref as T;
  }

  if (typeof ref === "string") {
    const trimmed = ref.trim();
    if (!trimmed) return undefined;

    // JSON-encoded manager object from some legacy payloads.
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          return parsed as T;
        }
      } catch {
        // fall through to plain PID handling
      }
    }

    // Plain admin PID string (e.g. "adm_xxx").
    return { [idKey]: trimmed } as T;
  }

  return undefined;
}

function pickTrimmed(
  enriched: unknown,
  fallback: unknown
): string | undefined {
  return trimString(enriched) || (typeof fallback === "string" ? fallback : undefined);
}

function mergeManager<T extends Record<string, unknown>>(
  idKey: keyof T,
  stringKeys: Array<keyof T>,
  enriched: T | undefined,
  refs: Array<T | string | undefined>
): T | undefined {
  let fromRef: T | undefined;
  for (const ref of refs) {
    fromRef = managerRefObject<T>(ref, idKey);
    if (fromRef) break;
  }

  if (!enriched && !fromRef) return undefined;

  const merged = { ...fromRef, ...enriched } as T;
  for (const key of stringKeys) {
    const value = pickTrimmed(enriched?.[key], fromRef?.[key]);
    if (value !== undefined) {
      merged[key] = value as T[keyof T];
    }
  }
  return merged;
}

function hasAssignedManager(name?: unknown, email?: unknown, phone?: unknown): boolean {
  const displayName = trimString(name);
  return Boolean(
    (displayName && displayName !== "Not Assigned") ||
      trimString(email) ||
      trimString(phone)
  );
}

/** Normalizes account manager fields from Go API string refs or legacy enriched objects. */
export function normalizeAccountManager(
  enriched?: AccountManager,
  ref?: AccountManager | string
): AccountManager | undefined {
  return mergeManager(
    "kam_id",
    ["kam_id", "kam_name", "kam_email", "kam_phone"],
    enriched as Record<string, unknown> | undefined,
    [ref]
  ) as AccountManager | undefined;
}

/** Normalizes business manager fields from Go API string refs or legacy enriched objects. */
export function normalizeBusinessManager(
  enriched?: BusinessManager,
  ...refs: Array<BusinessManager | string | undefined>
): BusinessManager | undefined {
  return mergeManager(
    "bm_id",
    ["bm_id", "bm_name", "bm_email", "bm_phone", "bm_status"],
    enriched as Record<string, unknown> | undefined,
    refs
  ) as BusinessManager | undefined;
}

export function hasAccountManager(manager?: AccountManager): boolean {
  return hasAssignedManager(manager?.kam_name, manager?.kam_email, manager?.kam_phone);
}

export function hasBusinessManager(manager?: BusinessManager): boolean {
  return hasAssignedManager(manager?.bm_name, manager?.bm_email, manager?.bm_phone);
}

/** Maps organisation manager fields from solutions-dashboard-service into UI-ready shapes. */
export function normalizeOrganisationManagers<T extends OrganisationManagerFields>(
  orgData: T
): T & {
  kam?: AccountManager;
  business_manager?: BusinessManager;
  bm?: BusinessManager;
} {
  const kam = normalizeAccountManager(orgData.kam, orgData.org_kam);
  const businessManager = normalizeBusinessManager(
    orgData.business_manager ?? orgData.bm,
    orgData.org_business_manager,
    orgData.org_bm
  );

  return {
    ...orgData,
    kam,
    business_manager: businessManager,
    bm: businessManager,
  };
}
